/*
 *  -----------------------------------------------------------------------
 *    <copyright file="risk-overview-scrollable-grid.helper.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { GridColumnDateFilterV2 } from '@components/core/grid-filters-v2';
import { useGridCells } from '@hooks/index';

const { CellWithChangeIcon, DateCell, StandardCell } = useGridCells();

const cleanUpFieldName = (text: string) => text.replaceAll(' ', '_').replaceAll('(', '').replaceAll(')', '');

export const getRiskOverviewGridColumns = (
    config: any,
    riskOverviewColumns: any,
    translate: (value: string) => string,
) => {
    const gridColumns = [
        {
            field: 'site',
            id: 'site-id',
            title: translate('cm:site'),
            minWidth: 110,
            cells: { data: StandardCell() },
            lockedByDefault: true,
            filter: null as any,
        },
    ] as any;

    const riskOverviewConfig = config?.riskOverview ?? {};

    if (riskOverviewConfig.siteStatus)
        gridColumns.push({
            field: 'siteStatus',
            id: 'site-status',
            title: translate('cm:siteStatus'),
            cells: { data: StandardCell() },
            minWidth: 130,
            filter: 'text',
        });

    if (riskOverviewConfig.numSubjects)
        gridColumns.push({
            field: 'numSubjects',
            id: 'num-subjects',
            title: translate('cm:numSubjects'),
            cells: { data: StandardCell() },
            minWidth: 120,
            filter: 'numeric',
            subjectField: riskOverviewConfig.subjectField,
        });

    gridColumns.push({
        field: 'score',
        id: 'site-score',
        title: translate('cm:siteScore'),
        minWidth: 120,
        filter: 'numeric',
        cells: { data: CellWithChangeIcon('siteScore', 2) },
    });

    if (riskOverviewConfig.redDecisionKris)
        gridColumns.push({
            field: 'redDecisionKris',
            id: 'red-decision-kris',
            title: translate('cm:redDecisionKris'),
            minWidth: 180,
            cells: { data: StandardCell(false, true) },
            filter: 'numeric',
            isWrapHeader: true,
        });

    if (riskOverviewConfig.amberDecisionKris)
        gridColumns.push({
            field: 'amberDecisionKris',
            id: 'amber-decision-kris',
            title: translate('cm:amberDecisionKris'),
            minWidth: 180,
            cells: { data: StandardCell(false, true) },
            filter: 'numeric',
            isWrapHeader: true,
        });

    if (riskOverviewConfig.redKris)
        gridColumns.push({
            field: 'redKris',
            id: 'red-kris',
            title: translate('cm:redKris'),
            minWidth: 120,
            cells: { data: StandardCell(false, true) },
            filter: 'numeric',
        });

    if (riskOverviewConfig.amberKris)
        gridColumns.push({
            field: 'amberKris',
            id: 'amber-kris',
            title: translate('cm:amberKris'),
            minWidth: 135,
            cells: { data: StandardCell(false, true) },
            filter: 'numeric',
        });

    (riskOverviewColumns ?? []).forEach((item: any) => {
        const fieldName = cleanUpFieldName(item.text);
        const tooltipValue = item.includeInSiteScoring ? `${translate('cm:weight')}: ${item.weight ?? 0}` : '';

        const labelValue = (
            <>
                {item.includeInSiteScoring ? (
                    <FontAwesomeIcon icon={['far', 'check-circle']} className="arrow-circle black" />
                ) : (
                    ''
                )}
                <span title={tooltipValue}> {item.text}</span>
            </>
        );

        const elementToPush = {
            field: `${fieldName}.score`,
            id: `${item.panelId}-id`,
            title: labelValue,
            text: item.text,
            panelId: item.panelId,
            fieldName,
            cells: { data: CellWithChangeIcon(fieldName, 2) },
            minWidth: 210,
            filter: 'numeric',
            tooltipValue,
            isWrapHeader: true,
        };

        gridColumns.push(elementToPush);
    });

    if (riskOverviewConfig.siteActiveDate)
        gridColumns.push({
            field: 'siteActiveDate',
            id: 'site-active-date',
            title: translate('cm:siteActiveDate'),
            minWidth: 150,
            cells: { data: DateCell },
            filter: 'date',
            filterUi: GridColumnDateFilterV2,
        });

    if (riskOverviewConfig.siteCountry)
        gridColumns.push({
            field: 'siteCountry',
            id: 'site-country',
            title: translate('cm:siteCountry'),
            minWidth: 135,
            cells: { data: StandardCell() },
            filter: 'text',
        });

    if (riskOverviewConfig.siteMonitor)
        gridColumns.push({
            field: 'siteMonitor',
            id: 'site-monitor',
            title: translate('cm:siteMonitor'),
            minWidth: 180,
            cells: { data: StandardCell() },
            filter: 'text',
        });

    return gridColumns;
};
